import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  CreditCard,
  Users,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  RefreshCw,
  DollarSign,
  Calendar,
  Phone,
  Mail,
  Store,
  ChevronDown,
  ChevronUp,
  MessageCircle
} from "lucide-react";
import toast from "react-hot-toast";

const API = window.location.origin;

export default function InstallmentManagementPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overdueUsers, setOverdueUsers] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState("installment");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [expandedSection, setExpandedSection] = useState("overdue");

  // جلب البيانات
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // جلب المتأخرين والقادمين
      const overdueRes = await fetch(`${API}/api/installment-payments/check-overdue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (overdueRes.ok) {
        const data = await overdueRes.json();
        setOverdueUsers(data.overdue_users || []);
        setUpcomingPayments(data.upcoming_payments || []);
      }
      
      // جلب جميع المستخدمين
      const usersRes = await fetch(`${API}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (usersRes.ok) {
        const users = await usersRes.json();
        setAllUsers(users.filter(u => u.role !== "super_admin"));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("فشل في جلب البيانات");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // تسجيل دفعة
  const recordPayment = async () => {
    if (!selectedUser) return;
    
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/installment-payments/record-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          payment_type: paymentType,
          amount_usd: paymentType === "full" ? 1000 : 400,
          payment_method: paymentMethod,
          notes: paymentNotes
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setShowPaymentModal(false);
        setSelectedUser(null);
        setPaymentNotes("");
        fetchData();
      } else {
        toast.error(data.detail || "فشل في تسجيل الدفعة");
      }
    } catch (error) {
      toast.error("حدث خطأ");
    }
  };

  // إيقاف حسابات المتأخرين
  const deactivateOverdue = async () => {
    if (!window.confirm("هل أنت متأكد من إيقاف حسابات جميع المتأخرين؟")) return;
    
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/installment-payments/deactivate-overdue`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(`تم إيقاف ${data.deactivated_count} حساب`);
        fetchData();
      }
    } catch (error) {
      toast.error("حدث خطأ");
    }
  };

  // فلترة المستخدمين
  const filteredUsers = allUsers.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.phone?.includes(searchQuery) ||
    u.shop_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // فتح واتساب
  const openWhatsApp = (phone, message) => {
    const cleanPhone = phone?.replace(/[^0-9]/g, "");
    const fullPhone = cleanPhone?.startsWith("0") ? `964${cleanPhone.substring(1)}` : cleanPhone;
    window.open(`https://wa.me/${fullPhone}?text=${encodeURIComponent(message)}`, "_blank");
  };

  // الحصول على حالة الدفع
  const getPaymentStatus = (user) => {
    const paymentInfo = user.payment_info;
    if (!paymentInfo || paymentInfo.payment_type === "none") {
      return { status: "none", label: "لم يدفع", color: "text-slate-400" };
    }
    if (paymentInfo.payment_type === "full") {
      return { status: "full", label: "دفع كامل", color: "text-emerald-400" };
    }
    if (paymentInfo.payment_type === "installment") {
      const paid = paymentInfo.installments_paid || 0;
      if (paid >= 3) {
        return { status: "complete", label: "مكتمل (3/3)", color: "text-emerald-400" };
      }
      return { status: "partial", label: `قسط ${paid}/3`, color: "text-amber-400" };
    }
    return { status: "unknown", label: "غير معروف", color: "text-slate-400" };
  };

  if (user?.role !== "super_admin" && user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-red-400">غير مصرح لك بالوصول لهذه الصفحة</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="installment-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-amber-500" />
            إدارة الأقساط
          </h1>
          <p className="text-slate-400 text-sm">متابعة مدفوعات العملاء والأقساط</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          تحديث
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-400">{overdueUsers.length}</p>
            <p className="text-slate-400 text-sm">متأخرين عن الدفع</p>
          </CardContent>
        </Card>
        
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-amber-400">{upcomingPayments.length}</p>
            <p className="text-slate-400 text-sm">أقساط قادمة (14 يوم)</p>
          </CardContent>
        </Card>
        
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-emerald-400">
              {allUsers.filter(u => u.payment_info?.payment_type === "full" || u.payment_info?.installments_paid >= 3).length}
            </p>
            <p className="text-slate-400 text-sm">دفعوا بالكامل</p>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-400">{allUsers.length}</p>
            <p className="text-slate-400 text-sm">إجمالي العملاء</p>
          </CardContent>
        </Card>
      </div>

      {/* المتأخرين عن الدفع */}
      <Card className="bg-slate-800/50 border-red-500/30">
        <CardHeader 
          className="cursor-pointer"
          onClick={() => setExpandedSection(expandedSection === "overdue" ? "" : "overdue")}
        >
          <CardTitle className="text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              المتأخرين عن الدفع ({overdueUsers.length})
            </div>
            {expandedSection === "overdue" ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSection === "overdue" && (
          <CardContent className="space-y-3">
            {overdueUsers.length === 0 ? (
              <p className="text-slate-400 text-center py-4">لا يوجد متأخرين 🎉</p>
            ) : (
              <>
                <div className="flex justify-end mb-2">
                  <Button onClick={deactivateOverdue} variant="destructive" size="sm" className="gap-2">
                    <XCircle className="w-4 h-4" />
                    إيقاف جميع المتأخرين
                  </Button>
                </div>
                {overdueUsers.map((u, i) => (
                  <div key={i} className="bg-red-500/10 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <p className="text-white font-bold">{u.name}</p>
                      <p className="text-slate-400 text-sm">{u.shop_name}</p>
                      <p className="text-red-400 text-sm">متأخر {u.days_overdue} يوم - قسط {u.installments_paid + 1}/3</p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openWhatsApp(u.phone, `مرحباً ${u.name}، نذكرك بموعد القسط المتأخر للتطبيق. يرجى التواصل معنا.`)}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => {
                          setSelectedUser(u);
                          setPaymentType("installment");
                          setShowPaymentModal(true);
                        }}
                      >
                        تسجيل دفعة
                      </Button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </CardContent>
        )}
      </Card>

      {/* الأقساط القادمة */}
      <Card className="bg-slate-800/50 border-amber-500/30">
        <CardHeader 
          className="cursor-pointer"
          onClick={() => setExpandedSection(expandedSection === "upcoming" ? "" : "upcoming")}
        >
          <CardTitle className="text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              أقساط قادمة خلال 14 يوم ({upcomingPayments.length})
            </div>
            {expandedSection === "upcoming" ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSection === "upcoming" && (
          <CardContent className="space-y-3">
            {upcomingPayments.length === 0 ? (
              <p className="text-slate-400 text-center py-4">لا توجد أقساط قادمة</p>
            ) : (
              upcomingPayments.map((u, i) => (
                <div key={i} className="bg-amber-500/10 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold">{u.name}</p>
                    <p className="text-slate-400 text-sm">{u.shop_name}</p>
                    <p className="text-amber-400 text-sm">
                      بعد {u.days_until_due} يوم - قسط {u.installments_paid + 1}/3
                    </p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => openWhatsApp(u.phone, `مرحباً ${u.name}، نذكرك بأن موعد القسط القادم للتطبيق بعد ${u.days_until_due} يوم.`)}
                  >
                    <MessageCircle className="w-4 h-4 me-1" />
                    تذكير
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        )}
      </Card>

      {/* جميع العملاء */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            جميع العملاء
          </CardTitle>
          <div className="relative mt-2">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="بحث بالاسم، الإيميل، الهاتف..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10 bg-slate-900/50"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {filteredUsers.map((u, i) => {
              const paymentStatus = getPaymentStatus(u);
              return (
                <div key={i} className="bg-slate-900/50 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{u.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full bg-slate-800 ${paymentStatus.color}`}>
                        {paymentStatus.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-slate-400 text-sm mt-1">
                      <span className="flex items-center gap-1">
                        <Store className="w-3 h-3" />
                        {u.shop_name || "-"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {u.phone || "-"}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedUser(u);
                      setPaymentType(paymentStatus.status === "none" ? "full" : "installment");
                      setShowPaymentModal(true);
                    }}
                  >
                    <DollarSign className="w-4 h-4 me-1" />
                    تسجيل دفعة
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Modal تسجيل الدفعة */}
      {showPaymentModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">تسجيل دفعة</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-900/50 rounded-lg p-3">
                <p className="text-white font-bold">{selectedUser.name}</p>
                <p className="text-slate-400 text-sm">{selectedUser.email}</p>
              </div>
              
              <div>
                <label className="text-slate-300 text-sm mb-2 block">نوع الدفع</label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={paymentType === "full" ? "default" : "outline"}
                    onClick={() => setPaymentType("full")}
                    className="w-full"
                  >
                    دفع كامل (690,000 د.ع)
                  </Button>
                  <Button
                    type="button"
                    variant={paymentType === "installment" ? "default" : "outline"}
                    onClick={() => setPaymentType("installment")}
                    className="w-full hidden"
                  >
                    قسط (غير متوفر)
                  </Button>
                </div>
              </div>
              
              <div>
                <label className="text-slate-300 text-sm mb-2 block">طريقة الدفع</label>
                <div className="grid grid-cols-3 gap-2">
                  {["cash", "bank", "card"].map(method => (
                    <Button
                      key={method}
                      type="button"
                      variant={paymentMethod === method ? "default" : "outline"}
                      onClick={() => setPaymentMethod(method)}
                      size="sm"
                    >
                      {method === "cash" ? "نقدي" : method === "bank" ? "حوالة" : "بطاقة"}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-slate-300 text-sm mb-2 block">ملاحظات (اختياري)</label>
                <Input
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="أي ملاحظات إضافية..."
                  className="bg-slate-900/50"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button onClick={recordPayment} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle className="w-4 h-4 me-2" />
                  تأكيد الدفع
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedUser(null);
                  }}
                >
                  إلغاء
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
